import { useEffect, useState } from 'react';
import api from '../services/api';
import SearchInput from '../components/SearchInput';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Edit2, Plus, Trash2, UserRound } from 'lucide-react';
import { toast } from 'sonner';

const NONE = '__none__';

const emptyForm = {
  email: '',
  name: '',
  password: '',
  defaultStockId: NONE,
  territoryIds: [],
};

const SellersPage = () => {
  const [sellers, setSellers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [stocks, setStocks] = useState([]);
  const [territories, setTerritories] = useState([]);

  useEffect(() => {
    loadSellers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const loadSellers = async () => {
    try {
      setLoading(true);
      const [response, stocksResponse, territoriesResponse] = await Promise.all([
        api.getUsers({ page: 1, limit: 100, search, role: 'USER' }),
        api.getStocks({ page: 1, limit: 100 }),
        api.getTerritories({ page: 1, limit: 100 }),
      ]);
      setSellers(response.data || []);
      setStocks(stocksResponse.data || []);
      setTerritories(territoriesResponse.data || []);
    } catch (error) {
      toast.error('Erro ao carregar vendedores');
      setSellers([]);
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (seller = null) => {
    setEditing(seller);
    setFormData(seller ? {
      email: seller.email,
      name: seller.name,
      password: '',
      defaultStockId: seller.defaultStockId || NONE,
      territoryIds: (seller.territories || []).map((item) => item.territoryId),
    } : emptyForm);
    setDialogOpen(true);
  };

  const toggleTerritory = (territoryId, checked) => {
    setFormData((current) => ({
      ...current,
      territoryIds: checked
        ? Array.from(new Set([...current.territoryIds, territoryId]))
        : current.territoryIds.filter((id) => id !== territoryId),
    }));
  };

  const save = async (event) => {
    event.preventDefault();
    const payload = {
      ...formData,
      defaultStockId: formData.defaultStockId === NONE ? null : formData.defaultStockId,
    };
    try {
      if (editing) {
        await api.updateUser(editing.id, {
          name: payload.name,
          defaultStockId: payload.defaultStockId,
          territoryIds: payload.territoryIds,
        });
        toast.success('Vendedor atualizado');
      } else {
        await api.createUser({ ...payload, role: 'USER' });
        toast.success('Vendedor criado');
      }
      setDialogOpen(false);
      setEditing(null);
      setFormData(emptyForm);
      loadSellers();
    } catch (error) {
      toast.error(error.message || 'Erro ao salvar vendedor');
    }
  };

  const toggle = async (seller) => {
    try {
      await api.toggleUser(seller.id);
      toast.success(`Vendedor ${seller.enabled ? 'desativado' : 'ativado'}`);
      loadSellers();
    } catch (error) {
      toast.error('Erro ao alterar status do vendedor');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Tem certeza que deseja remover este vendedor?')) return;
    try {
      await api.deleteUser(id);
      toast.success('Vendedor removido');
      loadSellers();
    } catch (error) {
      toast.error('Erro ao remover vendedor');
    }
  };

  return (
    <div className="space-y-6" data-testid="sellers-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Vendedores</h1>
          <p className="text-muted-foreground mt-1">Usuários do tipo vendedor</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => openDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Novo Vendedor
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? 'Editar Vendedor' : 'Novo Vendedor'}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-4">
              <div className="space-y-2">
                <Label>Nome</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Estoque padrão</Label>
                <Select value={formData.defaultStockId} onValueChange={(value) => setFormData({ ...formData, defaultStockId: value })}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Sem estoque padrão</SelectItem>
                    {stocks.map((stock) => <SelectItem key={stock.id} value={stock.id}>{stock.code} - {stock.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Carteiras</Label>
                <div className="max-h-44 overflow-y-auto rounded-md border">
                  {territories.length === 0 ? (
                    <div className="p-3 text-sm text-muted-foreground">Nenhuma carteira cadastrada.</div>
                  ) : territories.map((territory) => (
                    <label key={territory.id} className="flex cursor-pointer items-center gap-2 border-b p-3 text-sm last:border-b-0">
                      <Checkbox
                        checked={formData.territoryIds.includes(territory.id)}
                        onCheckedChange={(checked) => toggleTerritory(territory.id, Boolean(checked))}
                      />
                      <span className="font-mono">{territory.code}</span>
                      <span>{territory.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              {!editing && (
                <>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Senha</Label>
                    <Input type="password" minLength={6} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required />
                  </div>
                </>
              )}
              <Button type="submit" className="w-full">{editing ? 'Atualizar' : 'Cadastrar'}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <SearchInput value={search} onChange={setSearch} placeholder="Pesquisar por nome ou email..." searchFields={['Nome', 'Email']} />

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-6 py-3 text-left text-sm font-semibold">Nome</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Email</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Estoque padrão</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Carteiras</th>
                <th className="px-6 py-3 text-center text-sm font-semibold">Tipo</th>
                <th className="px-6 py-3 text-center text-sm font-semibold">Status</th>
                <th className="px-6 py-3 text-right text-sm font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody>
              {sellers.map((seller) => (
                <tr key={seller.id} className="border-b hover:bg-muted/50">
                  <td className="px-6 py-4 text-sm font-medium">{seller.name}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{seller.email}</td>
                  <td className="px-6 py-4 text-sm">{seller.defaultStock ? `${seller.defaultStock.code} - ${seller.defaultStock.name}` : '-'}</td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex flex-wrap gap-1">
                      {(seller.territories || []).length === 0 ? '-' : seller.territories.map((item) => (
                        <Badge key={item.id} variant="outline">{item.territory?.code}</Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-center"><Badge variant="secondary">Vendedor</Badge></td>
                  <td className="px-6 py-4 text-sm text-center">
                    <Switch checked={seller.enabled} onCheckedChange={() => toggle(seller)} />
                  </td>
                  <td className="px-6 py-4 text-sm text-right space-x-2">
                    <Button variant="ghost" size="sm" onClick={() => openDialog(seller)}><Edit2 className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(seller.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {!loading && sellers.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <UserRound className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Nenhum vendedor encontrado.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SellersPage;
